import {
  Badge,
  Button,
  Group,
  Modal,
  PasswordInput,
  Select,
  Stack,
  Switch,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { type AdminUser, useAdminMutation, useAdminQuery } from "../../api/admin";
import { api } from "../../api/client";
import { useAuth } from "../../auth/auth";
import { formatDateTime } from "../../lib/format";

interface UserForm {
  username: string;
  password: string;
  displayName: string;
  role: "admin" | "auditor";
  locale: "hy" | "ru" | "en";
  active: boolean;
}

const EMPTY: UserForm = {
  username: "",
  password: "",
  displayName: "",
  role: "auditor",
  locale: "hy",
  active: true,
};

export function UsersPage() {
  const { t } = useTranslation();
  const me = useAuth().user;
  const users = useAdminQuery<{ items: AdminUser[] }>("users", "/api/admin/users");
  const [editing, setEditing] = useState<AdminUser | null | "new">(null);
  const [form, setForm] = useState<UserForm>(EMPTY);
  const save = useAdminMutation(
    async (v: { id: number | null; form: UserForm }) => {
      if (v.id === null) {
        const { active: _a, ...body } = v.form;
        return api("/api/admin/users", { method: "POST", body });
      }
      const body: Record<string, unknown> = {
        displayName: v.form.displayName,
        role: v.form.role,
        locale: v.form.locale,
        active: v.form.active,
      };
      if (v.form.password) body.password = v.form.password;
      return api(`/api/admin/users/${v.id}`, { method: "PATCH", body });
    },
    ["users"],
  );

  const open = (u: AdminUser | "new") => {
    setEditing(u);
    setForm(
      u === "new"
        ? EMPTY
        : {
            username: u.username,
            password: "",
            displayName: u.displayName,
            role: u.role,
            locale: (u.locale as UserForm["locale"]) ?? "hy",
            active: u.active,
          },
    );
  };
  const isNew = editing === "new";
  const isSelf = editing !== null && editing !== "new" && editing.id === me?.id;

  return (
    <Stack gap="sm">
      <Group justify="flex-end">
        <Button onClick={() => open("new")}>{t("admin.users.add")}</Button>
      </Group>
      <Table.ScrollContainer minWidth={640}>
        <Table striped withTableBorder>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("auth.username")}</Table.Th>
              <Table.Th>{t("admin.users.displayName")}</Table.Th>
              <Table.Th>{t("admin.users.role")}</Table.Th>
              <Table.Th>{t("admin.users.lastLogin")}</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(users.data?.items ?? []).map((u) => (
              <Table.Tr key={u.id} style={{ opacity: u.active ? 1 : 0.5 }}>
                <Table.Td>
                  {u.username}
                  {!u.active && (
                    <Badge ml={6} size="xs" color="gray">
                      {t("admin.users.inactive")}
                    </Badge>
                  )}
                </Table.Td>
                <Table.Td>{u.displayName}</Table.Td>
                <Table.Td>{t(`auth.role.${u.role}`)}</Table.Td>
                <Table.Td>{u.lastLoginAt ? formatDateTime(u.lastLoginAt) : "—"}</Table.Td>
                <Table.Td>
                  <Button size="compact-xs" variant="subtle" onClick={() => open(u)}>
                    {t("common.edit")}
                  </Button>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
      <Modal
        opened={editing !== null}
        onClose={() => setEditing(null)}
        title={isNew ? t("admin.users.add") : t("admin.users.edit")}
      >
        <Stack>
          <TextInput
            label={t("auth.username")}
            value={form.username}
            onChange={(e) => setForm({ ...form, username: e.currentTarget.value })}
            disabled={!isNew}
            required
          />
          <TextInput
            label={t("admin.users.displayName")}
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.currentTarget.value })}
            required
          />
          <PasswordInput
            label={isNew ? t("auth.password") : t("admin.users.newPassword")}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.currentTarget.value })}
            description={t("admin.users.passwordHint")}
            required={isNew}
          />
          <Select
            label={t("admin.users.role")}
            data={[
              { value: "auditor", label: t("auth.role.auditor") },
              { value: "admin", label: t("auth.role.admin") },
            ]}
            value={form.role}
            onChange={(v) => v && setForm({ ...form, role: v as UserForm["role"] })}
            allowDeselect={false}
            disabled={isSelf}
          />
          <Select
            label={t("nav.language")}
            data={[
              { value: "hy", label: "Հայերեն" },
              { value: "ru", label: "Русский" },
              { value: "en", label: "English" },
            ]}
            value={form.locale}
            onChange={(v) => v && setForm({ ...form, locale: v as UserForm["locale"] })}
            allowDeselect={false}
          />
          {!isNew && (
            <Switch
              label={t("admin.users.active")}
              checked={form.active}
              onChange={(e) => setForm({ ...form, active: e.currentTarget.checked })}
              disabled={isSelf}
            />
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setEditing(null)}>
              {t("common.cancel")}
            </Button>
            <Button
              loading={save.isPending}
              disabled={!form.username || !form.displayName || (isNew && form.password.length < 8)}
              onClick={async () => {
                try {
                  await save.mutateAsync({ id: isNew ? null : (editing as AdminUser).id, form });
                  notifications.show({ message: t("common.saved"), color: "green" });
                  setEditing(null);
                } catch (e) {
                  notifications.show({ message: (e as Error).message, color: "red" });
                }
              }}
            >
              {t("common.save")}
            </Button>
          </Group>
          {!isNew && (
            <Text size="xs" c="dimmed">
              {t("admin.users.sessionsHint")}
            </Text>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
}
